import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationHttpService } from '../../../authentication/services/authentication-http.service';
import { LogService } from '../../../core/logger/services/log.service';
import { LayoutService } from '../../../layout/state/layout.service';
import { LayoutQuery } from '../../../layout/state/layout.query';
import { LayoutHttpService } from '../../../layout/services/layout-http.service';
import { UserService } from '../../services/user.service';
import { citiesByCountry } from '../../constants/role-api-url.constant';
import { animation } from '@angular/animations';

@Component({
  selector: 'app-user-regions',
  templateUrl: './user-regions.component.html',
  styleUrls: ['./user-regions.component.scss']
})
export class UserRegionsComponent implements OnInit, AfterViewInit {
  selectedCountry: string | null = null;
  userRegions: any[] = [];
  userOrganisations: any[] = [];
  allRegions: any[] = [];
  userAssignedRegions: any[] = [];
  userAssignedOrganisations: any[] = [];
  filteredOrganisations: any[] = [];
  selectedRegionId: number | null = null;
  selectedRegionName: string | null = null;
  isLoadingOrgs = false;
  map!: google.maps.Map;
  mapMarkers: google.maps.Marker[] = [];
  viewInitialized = false;
  @ViewChild('sitelistmap') mapDiv!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authHttpService: AuthenticationHttpService,
    private logService: LogService,
    public layoutService: LayoutService,
    public layoutQuery: LayoutQuery,
    private layoutHttpService: LayoutHttpService,
    private userService: UserService
  ) { }

  ngOnInit() {
    this.loadRegions();
    this.loadOrganisations();
    this.getUserRegions();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.allRegions.length) {
      this.initializeMap();
    }
  }

  initializeMap() {
    const mapOptions: google.maps.MapOptions = {
      center: new google.maps.LatLng(29.279141, 47.864518),
      zoom: 3,
      mapTypeControl: false,
    };
    this.map = new google.maps.Map(this.mapDiv.nativeElement, mapOptions);

    this.renderRegionMarkers();
  }

  renderRegionMarkers() {
    if (!this.map) return;
    this.mapMarkers.forEach(marker => (marker as any).setMap(null));
    this.mapMarkers = [];

    this.allRegions.forEach(region => {
      if (!region.latitude || !region.longitude) return;

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(region.latitude, region.longitude),
        map: this.map,
        title: region.regionName,
        icon: this.getMarkerIcon(region.id === this.selectedRegionId)
      });

      marker.addListener('click', () => this.onRegionClick(region));
      this.mapMarkers.push(marker);
    });
  }

  getMarkerIcon(isSelected: boolean): google.maps.Symbol {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: isSelected ? 'red' : 'blue',
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: 'white'
    };
  }

  onRegionClick(region: any) {
    this.selectedCountry = region.region;
    this.selectedRegionId = region.id;
    this.selectedRegionName = region.regionName;

    this.filteredOrganisations = [];
    this.isLoadingOrgs = true;

    if (this.map) {
      this.map.setZoom(7);
      this.map.panTo(new google.maps.LatLng(region.latitude, region.longitude));
    }
    this.showCityPins(region.region);
    this.getOrganisations(region.id);
  }

  showCityPins(regionCode: string) {
    const cities = citiesByCountry[regionCode];
    if (!cities || !this.map) return;

    cities.forEach((city: any) => {
      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(city.lat, city.lng),
        map: this.map,
        icon: {
          animation: google.maps.Animation.DROP,
          scale: 6,
          fillColor: '#008000',
          fillOpacity: 1,
          strokeWeight: 1,
          strokeColor: '#ffffff'
        }
      });
      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="font-weight:bold; font-size: 14px; padding: 2px 10px;">${city.name}</div>`
      });
      marker.addListener('mouseover', () => {
        infoWindow.open(this.map, marker);
      });

      marker.addListener('click', () => {
        if (city.orgId) {
          this.selectOrg(city.orgId);
        } else {
          console.warn(`No organization linked for city: ${city.name}`);
        }
      });

      this.mapMarkers.push(marker);
    });

    const bounds = new google.maps.LatLngBounds();
    cities.forEach((c: any) => bounds.extend(new google.maps.LatLng(c.lat, c.lng)));
    this.map.fitBounds(bounds);
  }


  showOrganisationPins(orgs: any[]) {
    if (!this.map) return;

    orgs.forEach(org => {
      if (!org.latitude || !org.longitude) return;

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(org.latitude, org.longitude),
        map: this.map,
        title: org.name,
        icon: {
          path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: 'green',
          fillOpacity: 1,
          strokeWeight: 1,
          strokeColor: 'white'
        }
      });

      marker.addListener('click', () => this.selectOrg(org.id));
      this.mapMarkers.push(marker);
    });
  }

  refreshMarkers() {
    this.renderRegionMarkers();
  }

  onResize() {
    if (this.map) {
      google.maps.event.trigger(this.map, 'resize');
    }
  }

  getUserRegions(): void {
    this.authHttpService.getUserInfo().subscribe({
      next: (userInfo) => {
        if (!userInfo) return;
        const userId = userInfo.sub;
        this.authHttpService.GetUserRegionsAsync(userId).subscribe({
          next: (assigned: any[]) => {
            const assignedIds = assigned.map(r => r.region);
            this.userAssignedRegions = this.allRegions
              .filter(r => assignedIds.includes(r.id))
              .map(r => ({
                ...r,
                primaryRegion: assigned.find(ar => ar.region === r.id)?.primaryRegion || false
              }));
          },
          error: (err) => console.error('Error fetching user regions:', err)
        });
      },
      error: (err) => console.error('Error fetching user info:', err)
    });
  }

  loadRegions(): void {
    this.userService.getRegions().subscribe({
      next: (regions: any[]) => {
        this.allRegions = regions.map(r => ({
          ...r,
          flagUrl: `assets/images/${r.region.toLowerCase()}-flag.jpg`,
          regionName: r.regionName || r.region,
          latitude: r.latitude || (r.region === 'KWT' ? 29.3759 : 24.7136),
          longitude: r.longitude || (r.region === 'KWT' ? 47.9774 : 46.6753)
        }));

        if (this.viewInitialized && !this.map) {
          this.initializeMap();
        }
        this.getUserRegions();
      }
    });
  }

  loadOrganisations(): void {
    this.userService.getOrganisations().subscribe({
      next: (response) => this.userOrganisations = response,
      error: (err) => console.error('Error loading organisations:', err)
    });
  }

  selectOrg(Id: number): void {
    localStorage.setItem('SelectedOrgId', JSON.stringify(Id));
    this.authHttpService.setMfaVerified(true);
    sessionStorage.removeItem('temp_token');
    this.router.navigate(['/reference/category']);
  }

  getOrganisations(regionId: number): void {
    this.isLoadingOrgs = true;
    this.filteredOrganisations = [];
    this.selectedRegionId = regionId;

    this.authHttpService.getUserInfo().subscribe({
      next: (userInfo) => {
        if (!userInfo) {
          this.isLoadingOrgs = false;
          return;
        }
        const userId = userInfo.sub;
        this.authHttpService.GetUserOrganisationsAsync(userId, regionId).subscribe({
          next: (assigned: any[]) => {
            const assignedOrgIds = assigned.map(o => o.orgId);

            this.filteredOrganisations = this.userOrganisations.filter(
              org => org.regionId === regionId && assignedOrgIds.includes(org.id)
            );

            this.showOrganisationPins(this.filteredOrganisations);

            this.isLoadingOrgs = false;
          },
          error: (err) => {
            console.error('Error fetching user organizations:', err);
            this.isLoadingOrgs = false;
          }
        });
      },
      error: (err) => {
        console.error('Error fetching user info:', err);
        this.isLoadingOrgs = false;
      }
    });
  }
}
